import { useEffect } from 'react';
import dynamic from 'next/dynamic';
import { Form } from 'react-aria-components';

import { SadFaceSVG } from 'components/iconsSVG/sadFace';

import { useI18n } from 'context/i18n';

import { subscribeTwsEvent, unsubscribeTwsEvent, conditionsTwsEvent } from 'actions/events';
import { checkNewsletterStatus } from 'actions/users';

import { ProtectionDataModal } from 'components/protectionDataModal';

import { useLocale } from 'hooks';
import { usePhonePrefix } from 'hooks/use-phone-prefix';
import { isBlank } from 'utils/StringUtils';
import { getDomainValue, isDomain } from 'utils/domain';

import { createStore } from '@xstate/store';
import { useSelector } from '@xstate/store/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryKeys } from 'utils/constants/reactQuery';
import { emailSchema, privacySchema } from 'utils/validation';
import { z } from 'zod/v4';
import { useForm } from 'hooks/use-form';
import { TextField } from 'components/ui/text-field';
import { Checkbox } from 'components/ui/checkbox';
import { Button } from 'components/ui/button';
import { Dialog, DialogHeader, DialogRoot } from 'components/ui/dialog';
import { Drawer, DrawerBody, DrawerHeader, DrawerRoot } from 'components/ui/drawer';
import { Label } from 'components/ui/field';
import { Select } from 'components/ui/select';
import { interpolate } from 'utils/string';

const ThankYouMessage = dynamic(() => import('components/thankYouMessage').then((mod) => mod.ThankYouMessage));
const PrivacyPolicyText = dynamic(() => import('components/legalTexts').then((mod) => mod.PrivacyPolicyText));

export type ShowEventsModalFn = (id?: string) => void;

type State = {
  open: boolean;
  id: string;
  step: 'subscribe' | 'unsubscribe' | 'thanks';
  email: string | null;
  thanks: 'eventSuscribedUserNew' | 'eventSuscribedAlready' | 'eventSuscribedFull' | 'eventUnsuscribed';
  isNew: boolean;
};

type EventMap = {
  open: { id: string; email?: string | null };
  thanks: { thanks: 'eventSuscribedUserNew' | 'eventSuscribedAlready' | 'eventSuscribedFull' | 'eventUnsuscribed' };
  set_email: { email: string };
  set_is_new: { isNew: boolean };
  close: {};
};

const store = createStore<State, EventMap, EventMap>({
  context: { open: false, id: '0', step: 'subscribe', thanks: 'eventSuscribedUserNew', isNew: false, email: null },
  on: {
    open: (context, event) => {
      return { ...context, open: true, id: event.id ?? '0', step: event.email ? 'unsubscribe' : 'subscribe', email: event.email ?? null };
    },
    thanks: (context, event) => {
      context.step = 'thanks';
      context.thanks = event.thanks;
    },
    set_email: (context, event) => {
      context.email = event.email;
    },
    set_is_new: (context, event) => {
      context.isNew = event.isNew;
    },
    close: (context) => {
      context.open = false;
    },
  },
});

function Unsubscribe() {
  const { t } = useI18n();
  const { locale } = useLocale();
  const id = useSelector(store, (state) => state.context.id);
  const email = useSelector(store, (state) => state.context.email);

  const { data: conditions } = useQuery({
    queryKey: [queryKeys.event, id],
    queryFn: () => conditionsTwsEvent({ profile: locale, eventId: id ?? '' }),
    enabled: id !== null,
  });

  const unsubscribe = useMutation({
    mutationFn: () => unsubscribeTwsEvent({ eventId: Number(id), email: email ?? '', profile: locale }),
    onSuccess: () => {
      store.send({ type: 'thanks', thanks: 'eventUnsuscribed' });
    },
  });

  return (
    <>
      <div>
        {getDomainValue({
          camper: <SadFaceSVG width={50} height={50} />,
          nnormal: <img src={'/assets-new/logo-nnormal.svg'} alt={'Nnormal Logo'} width={200} height={80} />,
          camperlab: <img src="/assets-new/svg/camperlab/camperlab-logo-black.svg" alt="Camperlab Logo" height={24} />,
        })}
      </div>
      <div
        className="mb-0 text-sm lab:uppercase lab:lg:text-xs"
        dangerouslySetInnerHTML={{ __html: conditions?.translations?.unsubscribeConfirm ?? t('generico', 'modal.events.tws.unsubscribe.confirm') }}
      />
      <p className="mb-0 text-sm lab:lg:text-xs">
        {conditions?.translations?.unsubscribeConfirmText ?? t('generico', 'modal.events.tws.unsubscribe.confirm.text')}
      </p>
      <Button isPending={unsubscribe.isPending} variant="outline" className="w-full" onPress={() => unsubscribe.mutate()}>
        {conditions?.translations?.unsubscribe ?? t('generico', 'tws.unsubscribe', 'Unsubscribe')}
      </Button>
    </>
  );
}

const eventSchema = z.object({
  name: z.string().optional(),
  email: emailSchema,
  privacy: z.stringbool().default(true),
  'subscribe-tws': privacySchema,
  phonePrefix: z.string().optional(),
  telephone: z.string().optional(),
});

function Subscribe() {
  const { t, profileData } = useI18n();
  const { locale, country: initialCountry } = useLocale();
  const { selectPrefixes, extractPrefix } = usePhonePrefix();
  const id = useSelector(store, (state) => state.context.id);
  const isNew = useSelector(store, (state) => state.context.isNew);

  const { data: conditions } = useQuery({
    queryKey: [queryKeys.event, id],
    queryFn: () => conditionsTwsEvent({ profile: locale, eventId: id ?? '' }),
    enabled: id !== null,
  });

  const { handleSubmit, validate } = useForm({ schema: eventSchema });

  const register = useMutation({
    mutationFn: async (values: z.infer<typeof eventSchema>) => {
      // set email in store
      store.send({ type: 'set_email', email: values.email });
      // check newsletter status
      const newsletterRs = await checkNewsletterStatus(locale, values.email);
      // if newsletter is not subscribed, set isNew to true
      if (!newsletterRs?.newsletterSubscribed && !isNew) {
        store.send({ type: 'set_is_new', isNew: true });
        return;
      }
      // subscribe to tws event
      const result = await subscribeTwsEvent({
        eventId: Number(id),
        name: values.name ?? '',
        email: values.email,
        phonePrefix: extractPrefix({ countryId: values.phonePrefix ?? '' }) ?? '',
        phone: values.telephone ?? '',
        profile: locale,
        lab: getDomainValue({ camper: false, nnormal: false, camperlab: true }),
      });
      const resultType =
        result?.message === 'already' ? 'eventSuscribedAlready'
        : result?.message === 'full' ? 'eventSuscribedFull'
        : newsletterRs?.newsletterSubscribed ? 'eventSuscribedAlready'
        : 'eventSuscribedUserNew';
      store.send({ type: 'thanks', thanks: resultType });
    },
  });

  return (
    <>
      {getDomainValue({
        camper: <img src="/assets-new/img/tws/tws_events.svg" alt="Members Only Logo" width={80} height={80} />,
        nnormal: <img src="/assets-new/logo-nnormal.svg" alt="Nnormal Logo" width={200} height={80} />,
        camperlab: <img src="/assets-new/svg/camperlab/camperlab-logo-black.svg" alt="Camperlab Logo" height={24} />,
      })}
      <p className="mb-0 text-2xl lab:text-sm lab:uppercase lab:lg:text-xs">
        {isNew ? (conditions?.translations?.titleNotMember ?? t('generico', 'modal.events.tws.title.not.member')) : (conditions?.translations?.title ?? '')}
      </p>
      <p
        className="mb-0 text-sm lab:lg:text-xs"
        dangerouslySetInnerHTML={{
          __html:
            isNew ?
              (conditions?.translations?.subtitleNotMember ?? t('generico', 'modal.events.tws.subtitle.not.member'))
            : (conditions?.translations?.subtitle ?? ''),
        }}
      />
      <Form autoComplete="off" onSubmit={(e) => register.mutate(handleSubmit(e))} className="flex w-full flex-col gap-4">
        {isNew ?
          <TextField
            label={t('compra.formulario', 'nombre')}
            hiddenLabel={getDomainValue({ camper: true, nnormal: true, camperlab: false })}
            name="name"
            type="text"
            inputProps={{
              variant: getDomainValue({ camper: 'transparent', nnormal: 'transparent', camperlab: 'default' }),
              placeholder: isDomain('camperlab') ? undefined : t('compra.formulario', 'nombre'),
            }}
          />
        : null}
        <TextField
          label={t('generico', 'input.email.placeholder', 'Enter your email')}
          hiddenLabel={getDomainValue({ camper: true, nnormal: true, camperlab: false })}
          name="email"
          type="email"
          validate={(value) => validate('email', value)}
          inputProps={{
            variant: getDomainValue({ camper: 'transparent', nnormal: 'transparent', camperlab: 'default' }),
            placeholder: isDomain('camperlab') ? undefined : t('generico', 'input.email.placeholder', 'Enter your email'),
          }}
        />
        <div>
          <div className="flex lab:flex-col lab:gap-2 camper:sm:w-2/3">
            <Select
              label={t('generico', 'prefijo.telefono', 'Telephone')}
              hiddenLabel={getDomainValue({ camper: true, nnormal: true, camperlab: false })}
              name="phonePrefix"
              items={selectPrefixes}
              defaultSelectedKey={initialCountry}
              variant={getDomainValue({ camper: 'transparent', nnormal: 'transparent', camperlab: 'default' })}
              className="flex-shrink-0 border-transparent bg-neutral-inverted/[0.025]"
            />
            <TextField
              name="telephone"
              label={t('generico', 'telefono', 'Telephone')}
              hiddenLabel={getDomainValue({ camper: true, nnormal: true, camperlab: false })}
              type="tel"
              className="flex-grow"
              inputProps={{
                placeholder: t('generico', 'placeholder.telefono'),
                variant: getDomainValue({ camper: 'transparent', nnormal: 'transparent', camperlab: 'default' }),
              }}
            />
          </div>
        </div>
        {isNew ?
          <div className="">
            <ProtectionDataModal origin="newsletter" overlay />
          </div>
        : null}
        {isNew ?
          <Checkbox name="privacy" isRequired boxClassName="size-5">
            {id === '398' ?
              <span
                className="text-xs lab:text-sm lab:lg:text-xs [&_a]:font-medium [&_a]:hover:cursor-pointer [&_a]:hover:underline"
                dangerouslySetInnerHTML={{
                  __html: interpolate(
                    t(
                      'event',
                      `privacy.policy${initialCountry === 'US' ? '.us' : ''}`,
                      'I\'ve read and accepted the <a href="<TAG privacy_policy_url>"> privacy policy</a> to become a member of The Walking Society and to receive commercial communications from Camper, including Newsletters and/or SMS',
                    ),
                    {
                      '<TAG privacy_policy_url>': profileData?.urlPrivacyPolicy,
                    },
                  ),
                }}
              />
            : <PrivacyPolicyText className="text-xs lab:text-sm lab:lg:text-xs [&_a]:font-medium [&_a]:hover:cursor-pointer [&_a]:hover:underline" />}
          </Checkbox>
        : null}
        <Checkbox name="subscribe-tws" isRequired boxClassName="size-5">
          <a href={conditions?.url} target="_blank" rel="noreferrer" className="text-xs hover:underline lab:text-sm lab:lg:text-xs">
            <span dangerouslySetInnerHTML={{ __html: t('generico', 'accept.suscribe.tws.checkbox') }}></span>
          </a>
        </Checkbox>
        {!isBlank(conditions?.translations?.extraInfo) && <div dangerouslySetInnerHTML={{ __html: conditions?.translations?.extraInfo ?? '' }} />}
        <div className="text-xs text-error empty:hidden">{register.error?.message ?? null}</div>
        <Button isPending={register.isPending} variant="outline" type="submit">
          {conditions?.translations?.subscribe ?? t('generico', 'tws.subscribe', 'Subscribe')}
        </Button>
      </Form>
    </>
  );
}

function NnormalIfSubscribe() {
  const { t, profileData } = useI18n();
  const { phonePrefixes } = profileData;
  const { locale, country: initialCountry } = useLocale();
  const id = useSelector(store, (state) => state.context.id);
  const isNew = useSelector(store, (state) => state.context.isNew);
  const { extractPrefix } = usePhonePrefix();

  const { data: conditions } = useQuery({
    queryKey: [queryKeys.event, id],
    queryFn: () => conditionsTwsEvent({ profile: locale, eventId: id }),
    enabled: id !== null,
  });

  const { handleSubmit, validate } = useForm({ schema: eventSchema });

  const register = useMutation({
    mutationFn: async (values: z.infer<typeof eventSchema>) => {
      // set email in store
      store.send({ type: 'set_email', email: values.email });

      // subscribe to tws event
      const result = await subscribeTwsEvent({
        eventId: Number(id),
        name: values.name ?? '',
        email: values.email,
        phonePrefix: extractPrefix({ countryId: values.phonePrefix ?? '' }) ?? '',
        phone: values.telephone ?? '',
        profile: locale,
        lab: getDomainValue({ camper: false, nnormal: false, camperlab: true }),
      });

      store.send({ type: 'thanks', thanks: 'eventSuscribedFull' });
    },
  });

  return (
    <>
      <img src="/assets-new/logo-nnormal.svg" alt="Nnormal Logo" width={200} height={80} />
      <p className="mb-0 text-2xl lab:text-sm lab:uppercase lab:lg:text-xs">{conditions?.translations?.title ?? ''}</p>
      <p
        className="mb-0 text-sm lab:lg:text-xs"
        dangerouslySetInnerHTML={{
          __html: conditions?.translations?.subtitle ?? '',
        }}
      />
      <Form autoComplete="off" onSubmit={(e) => register.mutate(handleSubmit(e))} className="flex w-full flex-col gap-4">
        <TextField
          label={t('generico', 'input.email.placeholder', 'Enter your email')}
          hiddenLabel
          name="email"
          type="email"
          validate={(value) => validate('email', value)}
          inputProps={{
            variant: getDomainValue({ camper: 'transparent', nnormal: 'transparent', camperlab: 'default' }),
            placeholder: isDomain('camperlab') ? undefined : t('generico', 'input.email.placeholder', 'Enter your email'),
          }}
        />
        <TextField
          label={t('compra.formulario', 'nombre')}
          hiddenLabel
          name="name"
          type="text"
          inputProps={{
            variant: getDomainValue({ camper: 'transparent', nnormal: 'transparent', camperlab: 'default' }),
            placeholder: isDomain('camperlab') ? undefined : t('compra.formulario', 'nombre'),
          }}
        />

        <div className="">
          <ProtectionDataModal origin="newsletter" overlay />
        </div>
        <Checkbox name="privacy" isRequired boxClassName="size-5">
          <PrivacyPolicyText className="text-xs lab:text-sm lab:lg:text-xs [&_a]:font-medium [&_a]:hover:cursor-pointer [&_a]:hover:underline" />
        </Checkbox>
        <Checkbox name="subscribe-killian" isRequired boxClassName="size-5">
          <span
            className="text-xs lab:text-sm lab:lg:text-xs [&_a]:font-medium [&_a]:hover:cursor-pointer [&_a]:hover:underline"
            dangerouslySetInnerHTML={{ __html: t('event', 'privacy.policy.extra') }}
          ></span>
        </Checkbox>
        <Checkbox name="subscribe-tws" isRequired boxClassName="size-5">
          <a href={conditions?.url} target="_blank" rel="noreferrer" className="text-xs hover:underline lab:text-sm lab:lg:text-xs">
            <span dangerouslySetInnerHTML={{ __html: t('generico', 'accept.suscribe.tws.checkbox') }}></span>
          </a>
        </Checkbox>
        {!isBlank(conditions?.translations?.extraInfo) && <div dangerouslySetInnerHTML={{ __html: conditions?.translations?.extraInfo ?? '' }} />}
        <div className="text-xs text-error empty:hidden">{register.error?.message ?? null}</div>
        <Button isPending={register.isPending} variant="outline" type="submit">
          {conditions?.translations?.subscribe ?? t('generico', 'tws.subscribe', 'Subscribe')}
        </Button>
      </Form>
    </>
  );
}

function Thanks() {
  const { t } = useI18n();
  const { locale } = useLocale();
  const thanks = useSelector(store, (state) => state.context.thanks);
  const id = useSelector(store, (state) => state.context.id);

  const { data: conditions } = useQuery({
    queryKey: [queryKeys.event, id],
    queryFn: () => conditionsTwsEvent({ profile: locale, eventId: id ?? '' }),
    enabled: id !== null,
  });

  return (
    <>
      <ThankYouMessage type={thanks} />
      {getDomainValue({
        camper:
          ['eventSuscribedFull', 'eventUnsuscribed'].includes(thanks) ?
            <>
              <p className="mb-2 mt-4">{conditions?.translations?.share ?? t('generico', 'modal.events.tws.share')}</p>
              <div className="flex items-center gap-4">
                <a
                  href={`https://web.whatsapp.com/send?text=https%3A%2F%2Fwww.camper.com%2F${locale}%2Fcontent%2Fnews%23${id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <img src="/assets-new/img/share/whatsapp.png" alt="whatsapp share" width={28} height={28} />
                </a>
                <a
                  href={`mailto:?subject=sharing link\n&body=I'm going to this event: https://www.camper.com/${locale}/content/news#${id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <img src="/assets-new/img/share/email.png" alt="email share" width={28} height={28} />
                </a>
              </div>
            </>
          : null,
        nnormal: null,
        camperlab: null,
      })}
    </>
  );
}

export function EventModal() {
  const { t } = useI18n();
  const step = useSelector(store, (state) => state.context.step);
  const id = useSelector(store, (state) => state.context.id);
  const isOpen = useSelector(store, (state) => state.context.open);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);

    const eventId = searchParams.get('eventId');
    const email = searchParams.get('email');
    if (eventId) {
      store.send({ type: 'open', id: eventId, email: email ? decodeURIComponent(email) : null });
    }
    const openFn = (id = '0') => {
      store.send({ type: 'open', id, email: null });
    };
    window.openEventModal = openFn;
    window.showEventsTwsModal = openFn;
    return () => {
      window.openEventModal = undefined;
      window.showEventsTwsModal = undefined;
    };
  }, []);

  const SubscribeComponent = isDomain('nnormal') && id === '269' ? <NnormalIfSubscribe /> : <Subscribe />;

  if (isDomain('camperlab')) {
    return (
      <DrawerRoot
        isOpen={isOpen}
        onOpenChange={() => {
          store.send({ type: 'close' });
        }}
        isDismissable
        showOverlay
      >
        <Drawer>
          <DrawerHeader label="Event Modal" hidden />
          <DrawerBody className="flex flex-col items-start gap-4">
            {step === 'subscribe' && SubscribeComponent}
            {step === 'unsubscribe' && <Unsubscribe />}
            {step === 'thanks' && <Thanks />}
          </DrawerBody>
        </Drawer>
      </DrawerRoot>
    );
  }

  return (
    <DialogRoot
      isOpen={isOpen}
      onOpenChange={() => {
        store.send({ type: 'close' });
      }}
      showOverlay
    >
      <Dialog>
        <DialogHeader label="Event Modal" hidden />
        <div className="mx-auto flex max-w-80 flex-col items-center gap-4 pb-8 pt-4 text-center lab:max-w-80 sm:pb-12 sm:pt-8">
          {step === 'subscribe' && SubscribeComponent}
          {step === 'unsubscribe' && <Unsubscribe />}
          {step === 'thanks' && <Thanks />}
        </div>
      </Dialog>
    </DialogRoot>
  );
}
